import { HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, of, throwError, timer } from 'rxjs';
import { delay, mergeMap } from 'rxjs/operators';
import { DocumentLink, DocumentLinkType, DraftKind, LineOfBusiness, QuoteSettings } from '../models/domain.models';
import { BoardId, ConnectorPeriod } from '../models/views.models';
import { ManualSettlementBody } from './commission-engine';
import { DraftBody, LegajoFileBody, ShareDocumentsBody, UnusualReportBody, UploadDocumentBody } from './compliance-engine';
import { ImportBody, StandalonePolicyBody } from './portfolio-engine';
import { AccountSaveBody, AuditQuery, CatalogDecision, TemplateBody } from './platform-engine';
import {
  AdjustOfferBody, ClaimBody, ClaimStatusBody, CommunicationBody, CreateQuoteBody, EndorsementBody, IssuanceBody,
  isCollectionName, LegajoBody, ManualPolicyBody, Outcome, PersonBody, ProtoStoreService, SelfRenewalBody, ShareQuoteBody,
} from './proto-store.service';

const PREFIX = '/api/proto/';
const PUBLIC_PREFIX = '/public/2/comparativo/';
const PUBLIC_DOCUMENTS_PREFIX = '/public/2/documentos/';

export function isProtoRequest(url: string): boolean {
  return url.includes(PREFIX);
}

export function isPublicRequest(url: string): boolean {
  return url.includes(PUBLIC_PREFIX) || url.includes(PUBLIC_DOCUMENTS_PREFIX);
}

function ok(body: unknown, status = 200): Observable<HttpResponse<unknown>> {
  // Copies only: a screen holding a signal over store objects would otherwise see them mutate in place.
  const copy = body === null || body === undefined ? null : structuredClone(body);
  return of(new HttpResponse({ status, body: copy })).pipe(delay(120));
}

function fail(status: number, code: string, message: string, url: string): Observable<never> {
  return timer(120).pipe(mergeMap(() => throwError(() => (
    { timestamp: new Date().toISOString(), status, error: 'Error', code, message, path: url }
  ))));
}

function respond<T>(outcome: Outcome<T>, url: string, status = 200): Observable<HttpResponse<unknown>> {
  return 'error' in outcome ? fail(outcome.error.status, outcome.error.code, outcome.error.message, url) : ok(outcome.value, status);
}

/** E-02: same token-scoped contract as E-01; request-mode uploads never reach the store (D-027-fe). */
function handlePublicDocuments(req: HttpRequest<unknown>, store: ProtoStoreService) {
  const token = req.url.slice(req.url.indexOf(PUBLIC_DOCUMENTS_PREFIX) + PUBLIC_DOCUMENTS_PREFIX.length).split('?')[0].split('/')[0];
  if (req.method === 'GET' && token) {
    const found = store.publicDocuments(token);
    return found ? ok(found) : fail(404, 'LINK_NOT_FOUND', 'El link no existe o ya no está disponible', req.url);
  }
  return fail(404, 'NOT_FOUND', `Sin handler para ${req.method} ${req.url}`, req.url);
}

/**
 * E-01 / E-02: anonymous, token-scoped. Never reads the signed-in user, and lives under `/public/2/` so the library
 * `errorInterceptor` does not send a visitor to `/login` on a 401 (S-FE-03).
 */
export function handlePublicRequest(req: HttpRequest<unknown>, store: ProtoStoreService) {
  if (req.url.includes(PUBLIC_DOCUMENTS_PREFIX)) return handlePublicDocuments(req, store);
  const path = req.url.slice(req.url.indexOf(PUBLIC_PREFIX) + PUBLIC_PREFIX.length).split('?')[0];
  const [token, action] = path.split('/').filter(Boolean);
  const body = (req.body ?? {}) as Record<string, unknown>;
  if (!token) return fail(404, 'NOT_FOUND', 'Link inexistente', req.url);
  if (req.method === 'GET' && !action) {
    const found = store.publicComparison(token);
    return found ? ok(found) : fail(404, 'LINK_NOT_FOUND', 'El link no existe o ya no está disponible', req.url);
  }
  if (req.method === 'POST' && action === 'choice') {
    const chosen = store.choosePublicOffer(token, String(body['companyOid'] ?? ''), String(body['message'] ?? ''));
    return chosen ? ok(chosen) : fail(404, 'LINK_NOT_FOUND', 'El link no existe o ya no está disponible', req.url);
  }
  return fail(404, 'NOT_FOUND', `Sin handler para ${req.method} ${path}`, req.url);
}

function filtersOf(req: HttpRequest<unknown>): Record<string, string> {
  return Object.fromEntries(
    req.params.keys().filter(k => k.startsWith('f.')).map(k => [k.slice(2), req.params.get(k) ?? '']),
  );
}

function handleCommissions(req: HttpRequest<unknown>, store: ProtoStoreService, parts: string[], body: Record<string, unknown>) {
  const [, second, third, fourth] = parts;
  const get = req.method === 'GET';
  if (get && second === 'summary') return ok(store.commissionSummary(filtersOf(req)));
  if (get && second === 'agreements') return ok(store.agreementsView());
  if (req.method === 'POST' && second === 'agreements') {
    return respond(store.saveAgreement(body as unknown as Parameters<typeof store.saveAgreement>[0]), req.url, 201);
  }
  if (get && second === 'split') return ok(store.splitView(req.params.get('period') ?? ''));
  if (req.method === 'POST' && second === 'splits') {
    return respond(store.saveSplit(body as unknown as Parameters<typeof store.saveSplit>[0]), req.url, 201);
  }
  if (get && second === 'mine') return ok(store.myCommission(req.params.get('period') ?? ''));
  if (req.method === 'POST' && second === 'settlements' && third === 'upload') {
    return respond(store.uploadSettlement(body as unknown as Parameters<typeof store.uploadSettlement>[0]), req.url, 201);
  }
  if (req.method === 'POST' && second === 'settlements' && third === 'manual') {
    return respond(store.manualSettlement(body as unknown as ManualSettlementBody), req.url, 201);
  }
  if (req.method === 'POST' && second === 'lines' && third && fourth === 'claim') {
    return respond(store.generateClaim(third), req.url, 201);
  }
  return fail(404, 'NOT_FOUND', `Sin handler para ${req.method} ${parts.join('/')}`, req.url);
}

function handleTree(req: HttpRequest<unknown>, store: ProtoStoreService, parts: string[], body: Record<string, unknown>) {
  const [, second, third, fourth, fifth, sixth] = parts;
  const get = req.method === 'GET';
  const post = req.method === 'POST';
  const text = (key: string) => String(body[key] ?? '');
  if (get && second === 'nodes') return ok(store.treeNodes());
  if (get && second === 'invitations' && !third) return ok(store.invitations());
  if (get && second === 'invitation-lookup') {
    return ok(store.lookupInvitation(req.params.get('email') ?? '', req.params.get('license') ?? '', req.params.get('taxId') ?? ''));
  }
  if (post && second === 'invitations' && !third) {
    return respond(store.invite(body as unknown as Parameters<typeof store.invite>[0]), req.url, 201);
  }
  if (post && second === 'invitations' && third && fourth === 'resend') return respond(store.resendInvitation(third), req.url);
  if (post && second === 'invitations' && third && fourth === 'accept') return respond(store.acceptInvitation(third), req.url);
  if (post && second === 'sellers') {
    return respond(store.createSeller(body as unknown as Parameters<typeof store.createSeller>[0]), req.url, 201);
  }
  if (second !== 'members' || !third) return fail(404, 'NOT_FOUND', `Sin handler para ${req.method} ${parts.join('/')}`, req.url);

  if (get && !fourth) return respond(store.memberCard(third), req.url);
  if (req.method === 'PUT' && !fourth) {
    return respond(store.editMember(third, body as unknown as Parameters<typeof store.editMember>[1]), req.url);
  }
  if (get && fourth === 'cascade') return respond(store.memberCascade(third), req.url);
  if (get && fourth === 'credentials' && !fifth) return respond(store.credentialsView(third), req.url);
  if (post && fourth === 'suspend') return respond(store.suspendMember(third), req.url);
  if (post && fourth === 'reactivate') return respond(store.reactivateMember(third), req.url);
  if (post && fourth === 'can-emit') return respond(store.setMemberCanEmit(third, body['canEmit'] === true), req.url);
  if (post && fourth === 'resend-invitation') return respond(store.resendInvitationOfMember(third), req.url);
  if (req.method === 'PUT' && fourth === 'codes') {
    return respond(store.saveOwnCodes(third, (body['codes'] ?? {}) as Record<string, string>), req.url);
  }
  if (fourth === 'credentials' && fifth) {
    if (req.method === 'PUT' && sixth === 'lines') {
      return respond(store.setCredentialLines(third, fifth, (body['enabledLines'] ?? []) as LineOfBusiness[]), req.url);
    }
    if (req.method === 'PUT' && !sixth) {
      return respond(store.saveCredential(third, fifth, {
        fields: (body['fields'] ?? {}) as Record<string, string>, producerCode: text('producerCode'),
        enabledLines: (body['enabledLines'] ?? []) as LineOfBusiness[],
      }), req.url);
    }
    if (post && sixth === 'validate') return respond(store.validateCredential(third, fifth), req.url);
    if (req.method === 'DELETE' && !sixth) return respond(store.deleteCredential(third, fifth), req.url);
  }
  return fail(404, 'NOT_FOUND', `Sin handler para ${req.method} ${parts.join('/')}`, req.url);
}

function auditQueryOf(req: HttpRequest<unknown>): AuditQuery {
  const f = filtersOf(req);
  return { user: f['user'], action: f['action'], entity: f['entity'], range: f['range'], treeOid: f['treeOid'] };
}

function pageOf(req: HttpRequest<unknown>): { page: number; size: number } {
  return { page: parseInt(req.params.get('page') ?? '1', 10), size: parseInt(req.params.get('size') ?? '20', 10) };
}

function handleAdmin(req: HttpRequest<unknown>, store: ProtoStoreService, parts: string[], body: Record<string, unknown>) {
  const [, second, third, fourth] = parts;
  const get = req.method === 'GET';
  const post = req.method === 'POST';
  if (get && second === 'summary') return respond(store.adminSummary(), req.url);
  if (get && second === 'trees') return respond(store.adminTrees(), req.url);
  if (get && second === 'connectors') return respond(store.connectorHealth((req.params.get('period') ?? '30d') as ConnectorPeriod), req.url);
  if (get && second === 'catalog') return respond(store.catalogReviews(), req.url);
  if (post && second === 'catalog' && third && fourth === 'confirm') {
    return respond(store.confirmCatalog(third, body as unknown as CatalogDecision), req.url);
  }
  if (post && second === 'catalog' && third && fourth === 'discontinue') return respond(store.discontinueCatalog(third), req.url);
  if (get && second === 'members' && third && fourth === 'cascade') return respond(store.adminCascade(third, req.params.get('action') ?? 'suspend'), req.url);
  if (post && second === 'members' && third && (fourth === 'suspend' || fourth === 'reactivate' || fourth === 'baja')) {
    return respond(store.adminMemberAction(third, fourth), req.url);
  }
  if (get && second === 'audit' && third === 'users') return ok(store.globalAuditUsers());
  if (get && second === 'audit') {
    const { page, size } = pageOf(req);
    return respond(store.globalAudit(auditQueryOf(req), page, size), req.url);
  }
  return fail(404, 'NOT_FOUND', `Sin handler para ${req.method} ${parts.join('/')}`, req.url);
}

function handlePortfolio(req: HttpRequest<unknown>, store: ProtoStoreService, parts: string[], body: Record<string, unknown>) {
  const [head, second, third] = parts;
  const get = req.method === 'GET';
  const post = req.method === 'POST';
  if (head === 'dashboard' && get) return respond(store.dashboard(filtersOf(req)), req.url);
  if (head === 'reports' && get && second) return respond(store.board(second as BoardId, filtersOf(req)), req.url);
  if (head === 'companies-view' && get) return ok(store.companiesView());
  if (head === 'catalog' && get && !second) return ok(store.catalogView());
  if (head === 'catalog' && post && second === 'hidden') return respond(store.setProductHidden(String(body['key'] ?? ''), body['hidden'] === true), req.url);
  if (head === 'imports' && get && second === 'companies') return ok(store.importCompanies());
  if (head === 'imports' && get && !second) return ok(store.importRuns());
  if (head === 'imports' && post && !second) return respond(store.runImport(body as unknown as ImportBody), req.url, 201);
  if (head === 'imports' && post && second && third === 'undo') return respond(store.undoImport(second), req.url);
  return fail(404, 'NOT_FOUND', `Sin handler para ${req.method} ${parts.join('/')}`, req.url);
}

function handleCompliance(req: HttpRequest<unknown>, store: ProtoStoreService, parts: string[], body: Record<string, unknown>) {
  const [head, second, third] = parts;
  const get = req.method === 'GET';
  const post = req.method === 'POST';
  const text = (key: string) => String(body[key] ?? '');
  if (head === 'document-entities' && get) {
    return ok(store.documentEntities((req.params.get('type') ?? 'policy') as DocumentLinkType, req.params.get('filter') ?? ''));
  }
  if (head === 'document-links' && get) return ok(store.documentShares());
  if (head === 'documents' && post && second === 'links') {
    return respond(store.shareDocuments(body as unknown as ShareDocumentsBody), req.url, 201);
  }
  if (head === 'documents' && post && !second) return respond(store.uploadDocument(body as unknown as UploadDocumentBody), req.url, 201);
  if (head === 'documents' && post && second && third === 'associate') {
    return respond(store.associateDocument(second, body as unknown as DocumentLink), req.url);
  }
  if (head === 'compliance' && second === 'legajo' && third) {
    return get ? respond(store.legajoView(third), req.url)
      : respond(store.saveComplianceFile(third, body as unknown as LegajoFileBody), req.url);
  }
  if (head === 'unusual-reports' && post && !second) return respond(store.createUnusualReport(body as unknown as UnusualReportBody), req.url, 201);
  if (head === 'unusual-reports' && req.method === 'PUT' && second) {
    return respond(store.updateUnusualReport(second, body as unknown as UnusualReportBody), req.url);
  }
  if (head === 'unusual-reports' && post && second && third === 'prepare') return respond(store.prepareUnusualReport(second), req.url);
  if (head === 'unusual-reports' && post && second && third === 'file') {
    return respond(store.fileUnusualReport(second, text('filedDate'), text('receiptNumber')), req.url);
  }
  if (head === 'regulatory' && second === 'emission-book') {
    const from = get ? req.params.get('from') ?? '' : text('from');
    const to = get ? req.params.get('to') ?? '' : text('to');
    return get ? respond(store.emissionBook(from, to), req.url) : respond(store.generateEmissionBook(from, to), req.url, 201);
  }
  if (head === 'drafts') {
    if (post && !second) return respond(store.saveDraft(body as unknown as DraftBody), req.url);
    if (get && !second) return ok(store.drafts((req.params.get('kind') as DraftKind | null) ?? null));
    if (get && second) return respond(store.draft(second), req.url);
    if (req.method === 'DELETE' && second) return respond(store.deleteDraft(second), req.url);
  }
  return fail(404, 'NOT_FOUND', `Sin handler para ${req.method} ${parts.join('/')}`, req.url);
}

function handleAccount(req: HttpRequest<unknown>, store: ProtoStoreService, parts: string[], body: Record<string, unknown>) {
  const [, second, third] = parts;
  const get = req.method === 'GET';
  const text = (key: string) => String(body[key] ?? '');
  if (get && !second) return respond(store.accountView(), req.url);
  if (req.method === 'PUT' && !second) return respond(store.saveAccount(body as unknown as AccountSaveBody), req.url);
  if (req.method === 'POST' && second === 'two-factor') return respond(store.setTwoFactor(body['enabled'] === true, text('code')), req.url);
  if (req.method === 'POST' && second === 'password') return respond(store.changePassword(text('current'), text('next')), req.url);
  if (req.method === 'POST' && second === 'templates') return respond(store.saveTemplate(body as unknown as TemplateBody), req.url, 201);
  if (req.method === 'DELETE' && second === 'templates' && third) return respond(store.deleteTemplate(third), req.url);
  return fail(404, 'NOT_FOUND', `Sin handler para ${req.method} ${parts.join('/')}`, req.url);
}

/** Dispatches `/api/proto/**` to the in-memory store; unknown paths answer 404. */
export function handleProtoRequest(req: HttpRequest<unknown>, store: ProtoStoreService) {
  const path = req.url.slice(req.url.indexOf(PREFIX) + PREFIX.length).split('?')[0];
  const parts = path.split('/').filter(Boolean);
  const [head, second, third, fourth] = parts;
  const body = (req.body ?? {}) as Record<string, unknown>;

  const blocked = store.writeBlockedMessage(req.method, path);
  if (blocked) return fail(403, 'SUPPORT_READ_ONLY', blocked, req.url);

  if (req.method === 'GET' && path === 'session/trees') return ok(store.treeChoices());
  if (head === 'admin') return handleAdmin(req, store, parts, body);
  if (head === 'account') return handleAccount(req, store, parts, body);
  if (
    head === 'document-entities' || head === 'document-links' || head === 'compliance' || head === 'regulatory' || head === 'drafts'
    || (head === 'unusual-reports' && req.method !== 'GET')
    || (head === 'documents' && req.method !== 'GET')
  ) {
    return handleCompliance(req, store, parts, body);
  }
  if (head === 'dashboard' || head === 'reports' || head === 'companies-view' || head === 'catalog' || head === 'imports') {
    return handlePortfolio(req, store, parts, body);
  }
  if (head === 'policies' && req.method === 'POST' && parts.length === 1) {
    return respond(store.registerStandalonePolicy(body as unknown as StandalonePolicyBody), req.url, 201);
  }
  if (head === 'quote-settings') {
    return req.method === 'GET' ? respond(store.quoteSettings(), req.url)
      : respond(store.saveQuoteSettings(body as unknown as QuoteSettings), req.url);
  }
  if (req.method === 'GET' && head === 'audit') {
    if (second === 'users') return ok(store.myAuditUsers());
    if (second === 'support-accesses') return ok(store.mySupportAccesses());
    const { page, size } = pageOf(req);
    return ok(store.myAudit(auditQueryOf(req), page, size));
  }

  if (req.method === 'GET' && path === 'session/counters') return ok(store.counters());
  if (req.method === 'GET' && path === 'clock') return ok(store.clock());
  if (req.method === 'POST' && path === 'clock/advance') {
    const days = Math.max(1, Math.min(365, Number(body['days'] ?? 1)));
    return ok(store.advanceClock(days));
  }
  if (req.method === 'GET' && path === 'tree') return ok(store.tree());
  if (req.method === 'GET' && path === 'companies') return ok(store.companies());
  if (req.method === 'GET' && path === 'vehicle-catalog') return ok(store.vehicleCatalog());
  if (req.method === 'GET' && path === 'quoting-companies') {
    return ok(store.quotingCompanies((req.params.get('line') ?? 'auto') as LineOfBusiness));
  }
  if (req.method === 'GET' && head === 'person-lookup' && second) return ok(store.lookupPerson(second));

  if (head === 'persons' && req.method === 'POST' && parts.length === 1) {
    const created = store.createPerson(body as unknown as PersonBody);
    return created ? ok(created, 201)
      : fail(409, 'PERSON_DUPLICATE', 'Ya existe una persona con ese documento en el árbol', req.url);
  }
  if (head === 'persons' && req.method === 'PUT' && parts.length === 2) {
    const updated = store.updatePerson(second, body as unknown as PersonBody);
    return updated ? ok(updated) : fail(404, 'NOT_FOUND', 'Persona inexistente', req.url);
  }
  if (head === 'persons' && req.method === 'PUT' && third === 'legajo') {
    const saved = store.saveLegajo(second, body as unknown as LegajoBody);
    return saved ? ok(saved) : fail(404, 'NOT_FOUND', 'Persona inexistente', req.url);
  }

  if (head === 'quotes' && req.method === 'POST' && parts.length === 1) {
    const quote = store.createQuote(body as unknown as CreateQuoteBody);
    return quote ? ok(quote, 201) : fail(400, 'QUOTE_INVALID', 'No se pudo crear la cotización', req.url);
  }
  if (head === 'quotes' && req.method === 'POST' && third === 'offers' && fourth) {
    const result = parts[4] === 'adjust'
      ? store.adjustOffer(second, fourth, body as unknown as AdjustOfferBody)
      : store.retryOffer(second, fourth);
    return result ? ok(result) : fail(404, 'NOT_FOUND', 'Oferta inexistente', req.url);
  }
  if (head === 'quotes' && req.method === 'POST' && second && third) {
    const invalid = fail(400, 'QUOTE_ACTION_INVALID', 'La acción no se puede aplicar a esta cotización', req.url);
    switch (third) {
      case 'choose': {
        const chosen = body['companyOid'] ? String(body['companyOid']) : null;
        const quote = store.chooseOffer(second, chosen);
        return quote ? ok(quote) : invalid;
      }
      case 'recotize': {
        const quote = store.recotizeQuote(second);
        return quote ? ok(quote) : invalid;
      }
      case 'desist': {
        const quote = store.desistQuote(second, String(body['reason'] ?? ''));
        return quote ? ok(quote) : invalid;
      }
      case 'hand-over': {
        const quote = store.handOverQuote(second);
        return quote ? ok(quote) : invalid;
      }
      case 'share': {
        const quote = store.shareQuote(second, body as unknown as ShareQuoteBody);
        return quote ? ok(quote) : invalid;
      }
      case 'requote': {
        const result = store.requote(second, String(body['companyOid'] ?? ''));
        return result ? ok(result) : invalid;
      }
      case 'manual-policy': {
        const policy = store.registerManualPolicy(second, body as unknown as ManualPolicyBody);
        return policy ? ok(policy, 201) : invalid;
      }
    }
  }
  if (head === 'quotes' && req.method === 'GET' && third === 'issuance-context') {
    const context = store.issuanceContext(second, req.params.get('companyOid') ?? '');
    return context ? ok(context) : fail(404, 'NOT_FOUND', 'Cotización inexistente', req.url);
  }
  if (head === 'issuance-requests' && req.method === 'POST' && parts.length === 1) {
    const request = store.createIssuance(body as unknown as IssuanceBody);
    return request ? ok(request, 201) : fail(400, 'ISSUANCE_INVALID', 'No se pudo solicitar la emisión', req.url);
  }

  if (head === 'policies' && second && third) {
    if (req.method === 'GET' && third === 'extras') return respond(store.policyExtras(second), req.url);
    if (req.method === 'GET' && third === 'as-of') {
      const asOf = store.policyAsOf(second, req.params.get('date') ?? store.today());
      return asOf ? ok(asOf) : fail(404, 'NOT_FOUND', 'Póliza inexistente', req.url);
    }
    if (req.method === 'POST') {
      switch (third) {
        case 'sync': return respond(store.syncPolicy(second), req.url);
        case 'certificate': return respond(store.requestCertificate(second), req.url, 201);
        case 'renewal-quote': return respond(store.createRenewalQuote(second), req.url);
        case 'self-renewal': return respond(store.registerSelfRenewal(second, body as unknown as SelfRenewalBody), req.url, 201);
        case 'not-renewing': return respond(store.setNotRenewing(second, String(body['reason'] ?? '')), req.url);
      }
    }
  }
  if (head === 'endorsements' && req.method === 'POST' && parts.length === 1) {
    return respond(store.createEndorsement(body as unknown as EndorsementBody), req.url, 201);
  }
  if (head === 'claims' && req.method === 'POST') {
    if (parts.length === 1) return respond(store.createClaim(body as unknown as ClaimBody), req.url, 201);
    if (third === 'status') return respond(store.changeClaimStatus(second, body as unknown as ClaimStatusBody), req.url);
    if (third === 'refresh') return respond(store.refreshClaim(second), req.url);
    if (third === 'documents' && fourth) return respond(store.receiveClaimDocument(second, fourth), req.url);
  }
  if (head === 'communications' && req.method === 'POST' && parts.length === 1) {
    return respond(store.createCommunication(body as unknown as CommunicationBody), req.url, 201);
  }

  if (head === 'commissions') return handleCommissions(req, store, parts, body);
  if (head === 'tree' && second) return handleTree(req, store, parts, body);

  if (req.method === 'GET' && path === 'installments/summary') return ok(store.installmentsSummary());
  if (req.method === 'POST' && path === 'notifications/read-all') return ok({ updated: store.markAllNotificationsRead() });
  if (req.method === 'POST' && head === 'notifications' && third === 'read' && second) {
    const updated = store.markNotificationRead(second);
    return updated ? ok(updated) : fail(404, 'NOT_FOUND', 'Notificación inexistente', req.url);
  }

  if (req.method === 'GET' && head && isCollectionName(head)) {
    if (second) {
      const found = store.find(head, second);
      return found ? ok(found) : fail(404, 'NOT_FOUND', 'No encontrado', req.url);
    }
    const page = parseInt(req.params.get('page') ?? '1', 10);
    const size = parseInt(req.params.get('size') ?? '20', 10);
    const filter = (req.params.get('filter') ?? '').toLowerCase();
    const filters = Object.fromEntries(
      req.params.keys().filter(k => k.startsWith('f.')).map(k => [k.slice(2), req.params.get(k) ?? '']),
    );
    return ok(store.list(head, page, size, filter, filters));
  }

  return fail(404, 'NOT_FOUND', `Sin handler para ${req.method} ${path}`, req.url);
}
